import { useEffect } from 'react'
import type { AsyncState } from './useAsync'
import type { MarketSurvey } from '../types'

/**
 * How far a survey has got, in the words the screens use (STE-888, STE-890).
 *
 * A 30,000 row upload said only "Still being stored" for its whole run, and
 * once stored nothing said whether it could be matched against yet. These
 * turn what the surveys list carries into one line each.
 */

/** "12,000 of 30,000 rows stored" while a survey fills, or null where that cannot be said. */
export function storedLine(survey: MarketSurvey): string | null {
  if (!survey.storing || !survey.progress) return null
  return `${survey.progress.stored.toLocaleString()} of ${survey.progress.of.toLocaleString()} rows stored`
}

/**
 * The same, for every survey still filling, said as one figure.
 *
 * Null if any of them cannot say, so the screen falls back to "Still being
 * stored" rather than giving a figure for part of what is going in.
 */
export function storedAcross(surveys: MarketSurvey[]): string | null {
  const filling = surveys.filter((s) => s.storing)
  if (!filling.length || filling.some((s) => !s.progress)) return null
  const stored = filling.reduce((n, s) => n + Math.min(s.progress!.stored, s.progress!.of), 0)
  const of = filling.reduce((n, s) => n + s.progress!.of, 0)
  return `${stored.toLocaleString()} of ${of.toLocaleString()} rows stored`
}

/**
 * Whether a survey can be matched against yet, once its rows are in.
 *
 * The share never reads 100% while preparing is still going, because the
 * last page can take a while and "100%" beside "Preparing" reads as stuck.
 * Null while it is still being stored, and for a survey stored before this
 * was kept, which says nothing rather than guessing.
 */
export function matchingLine(survey: MarketSurvey): string | null {
  if (survey.storing) return null
  const p = survey.preparation
  if (!p) return null
  switch (p.state) {
    case 'preparing': {
      if (!p.of) return 'Preparing for matching'
      return `Preparing for matching, ${Math.min(99, Math.floor((p.rows / p.of) * 100))}% done`
    }
    case 'ready': return 'Ready for matching'
    case 'empty': return 'Nothing to match against'
    case 'stopped': return 'Preparing for matching stopped. Our team has been told.'
    default: return null
  }
}

/** Whether anything on screen is still moving, so it is worth looking again. */
export function busy(surveys: MarketSurvey[]): boolean {
  return surveys.some((s) => s.storing || s.preparation?.state === 'preparing')
}

/** How often a screen looks again while a survey is being stored or prepared. */
export const LOOK_AGAIN_MS = 5000

/**
 * Looks at the surveys again every few seconds while one is busy (FR-M62).
 *
 * The list is replaced in place, without the loading state a reload shows, so
 * the figure moves and nothing else on the screen flickers. It stops looking
 * as soon as nothing is busy, and on leaving the screen.
 */
export function useLookAgain(surveys: AsyncState<MarketSurvey[]>, load: () => Promise<MarketSurvey[]>): void {
  const moving = busy(surveys.data ?? [])
  useEffect(() => {
    if (!moving) return
    let live = true
    const timer = window.setInterval(() => {
      load()
        .then((next) => { if (live) surveys.set(next) })
        // A look that fails is simply not shown. The next one tries again.
        .catch(() => {})
    }, LOOK_AGAIN_MS)
    return () => {
      live = false
      window.clearInterval(timer)
    }
    // The list itself changing is what this sets; depending on it would restart the timer each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moving])
}
